import React, { useEffect, useState, useRef } from 'react';
import { Header } from '../../components/layout/Header';
import { reportsApi, adminApi } from '../../lib/api';
import { toast } from 'sonner';
import { formatDate, formatDateRange, formatNumber } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { 
    FileText, 
    Plus, 
    Upload, 
    Download,
    Edit,
    Trash2,
    Calendar,
    FileSpreadsheet,
    Linkedin,
    Mail,
    Megaphone,
    TrendingUp,
    UserPlus,
    X,
    RefreshCw
} from 'lucide-react';

// Standalone component — must be outside parent to avoid unmount/remount on every keystroke
const NumericField = React.memo(({ label, value, onChange, accent }) => (
    <div className="space-y-1">
        <Label className={`text-xs ${accent === 'red' ? 'text-brand-red font-bold' : accent === 'green' ? 'text-status-success font-bold' : 'text-content-secondary'}`}>{label}</Label>
        <Input
            type="text"
            inputMode="numeric"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={`input-base h-9 ${accent === 'red' ? 'border-brand-red/30' : accent === 'green' ? 'border-status-success/30' : ''}`}
            placeholder="0"
        />
    </div>
));

const emptyProfile = {
    profile_name: '',
    reachouts_sent: 0,
    connection_accepted: 0,
    responses: 0,
    positive_responses: 0,
    rejections: 0,
    in_progress: 0,
    mqls: 0,
    qualified_leads: 0,
    target_industries: '',
    target_company_sizes: '',
    target_designations: '',
};

const initialFormState = {
    customer_id: '',
    report_type: 'weekly',
    period_start: '',
    period_end: '',
    // Overall Metrics
    target_accounts: 0,
    total_contacts: 0,
    total_reachouts: 0,
    responses: 0,
    positive_responses: 0,
    rejects: 0,
    future_leads: 0,
    mqls: 0,
    qualified_leads: 0,
    // LinkedIn - Overall fields
    linkedin_target_accounts: 0,
    linkedin_target_contacts: 0,
    linkedin_reachouts_sent: 0,
    linkedin_connection_accepted: 0,
    linkedin_responses: 0,
    linkedin_positive_responses: 0,
    linkedin_rejections: 0,
    linkedin_in_progress: 0,
    linkedin_mqls: 0,
    linkedin_qualified_leads: 0,
    // LinkedIn Profiles
    linkedin_profiles: [],
    // Email - ALL fields
    email_sent: 0,
    email_delivered: 0,
    email_opened: 0,
    email_clicked: 0,
    email_replies: 0,
    email_positive_replies: 0,
    email_mqls: 0,
    email_qualified_leads: 0,
    // Ads - ALL fields
    ads_accounts_targeted: 0,
    ads_impressions: 0,
    ads_clicks: 0,
    ads_ctr: 0,
    ads_account_reach_percent: 0,
    ads_engagement_rate: 0,
    ads_cost_per_engaged_account: 0,
    ads_cost_per_mql: 0,
    ads_influenced_pipeline: 0,
    // Inbound - ALL fields
    inbound_total_views: 0,
    inbound_total_engagement: 0,
    inbound_total_leads: 0,
    // Insights
    insights: '',
    risks_blockers: '',
    recommendations: '',
    // Goals
    goal_target_accounts: 0,
    goal_total_contacts: 0,
    goal_total_reachouts: 0,
    goal_responses: 0,
    goal_mqls: 0,
    goal_qualified_leads: 0,
};

export default function AdminReportsPage() {
    const [loading, setLoading] = useState(true);
    const [reports, setReports] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingReport, setEditingReport] = useState(null);
    const [saving, setSaving] = useState(false);
    const [csvFile, setCsvFile] = useState(null);
    const csvInputRef = useRef(null);
    const [reportForm, setReportForm] = useState(initialFormState);

    useEffect(() => {
        loadData();
    }, [selectedCustomer]);

    const loadData = async () => {
        try {
            const [customersRes, reportsRes] = await Promise.all([
                adminApi.getCustomers(),
                reportsApi.getAll(null, selectedCustomer || undefined),
            ]);
            setCustomers(customersRes.data);
            setReports(reportsRes.data);
        } catch (error) {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenCreate = () => {
        setEditingReport(null);
        setReportForm({
            ...initialFormState,
            customer_id: selectedCustomer || (customers[0]?.details?.id || ''),
            period_start: new Date().toISOString().split('T')[0],
            period_end: new Date().toISOString().split('T')[0],
            linkedin_profiles: [],
        });
        setDialogOpen(true);
    };

    const handleOpenEdit = (report) => {
        setEditingReport(report);
        setReportForm({
            customer_id: report.customer_id,
            report_type: report.report_type,
            period_start: report.period_start.split('T')[0],
            period_end: report.period_end.split('T')[0],
            // Overall Metrics
            target_accounts: report.metrics?.target_accounts || 0,
            total_contacts: report.metrics?.total_contacts || 0,
            total_reachouts: report.metrics?.total_reachouts || 0,
            responses: report.metrics?.responses || 0,
            positive_responses: report.metrics?.positive_responses || 0,
            rejects: report.metrics?.rejects || 0,
            future_leads: report.metrics?.future_leads || 0,
            mqls: report.metrics?.mqls || 0,
            qualified_leads: report.metrics?.qualified_leads || 0,
            // LinkedIn
            linkedin_target_accounts: report.linkedin?.target_accounts || 0,
            linkedin_target_contacts: report.linkedin?.target_contacts || 0,
            linkedin_reachouts_sent: report.linkedin?.reachouts_sent || 0,
            linkedin_connection_accepted: report.linkedin?.connection_accepted || 0,
            linkedin_responses: report.linkedin?.responses || 0,
            linkedin_positive_responses: report.linkedin?.positive_responses || 0,
            linkedin_rejections: report.linkedin?.rejections || 0,
            linkedin_in_progress: report.linkedin?.in_progress || 0,
            linkedin_mqls: report.linkedin?.mqls || 0,
            linkedin_qualified_leads: report.linkedin?.qualified_leads || 0,
            linkedin_profiles: (report.linkedin?.profiles || []).map(p => ({
                ...p,
                target_industries: Array.isArray(p.target_industries) ? p.target_industries.join(', ') : (p.target_industries || ''),
                target_company_sizes: Array.isArray(p.target_company_sizes) ? p.target_company_sizes.join(', ') : (p.target_company_sizes || ''),
                target_designations: Array.isArray(p.target_designations) ? p.target_designations.join(', ') : (p.target_designations || ''),
            })),
            // Email
            email_sent: report.email?.emails_sent || 0,
            email_delivered: report.email?.delivered || 0,
            email_opened: report.email?.opened || 0,
            email_clicked: report.email?.clicked || 0,
            email_replies: report.email?.replies || 0,
            email_positive_replies: report.email?.positive_replies || 0,
            email_mqls: report.email?.mqls || 0,
            email_qualified_leads: report.email?.qualified_leads || 0,
            // Ads
            ads_accounts_targeted: report.ads?.accounts_targeted || 0,
            ads_impressions: report.ads?.impressions || 0,
            ads_clicks: report.ads?.clicks || 0,
            ads_ctr: report.ads?.ctr || 0,
            ads_account_reach_percent: report.ads?.account_reach_percent || 0,
            ads_engagement_rate: report.ads?.engagement_rate || 0,
            ads_cost_per_engaged_account: report.ads?.cost_per_engaged_account || 0,
            ads_cost_per_mql: report.ads?.cost_per_mql || 0,
            ads_influenced_pipeline: report.ads?.influenced_pipeline || 0,
            // Inbound
            inbound_total_views: report.inbound?.total_views || 0,
            inbound_total_engagement: report.inbound?.total_engagement || 0,
            inbound_total_leads: report.inbound?.total_leads || 0,
            // Insights
            insights: report.insights?.join('\n') || '',
            risks_blockers: report.risks_blockers?.join('\n') || '',
            recommendations: report.recommendations?.join('\n') || '',
            // Goals
            goal_target_accounts: report.goals?.target_accounts || 0,
            goal_total_contacts: report.goals?.total_contacts || 0,
            goal_total_reachouts: report.goals?.total_reachouts || 0,
            goal_responses: report.goals?.responses || 0,
            goal_mqls: report.goals?.target_mqls || report.goals?.mqls || 0,
            goal_qualified_leads: report.goals?.target_qualified_leads || report.goals?.qualified_leads || 0,
        });
        setDialogOpen(true);
    };

    const addLinkedInProfile = () => {
        setReportForm(prev => ({
            ...prev,
            linkedin_profiles: [...prev.linkedin_profiles, { ...emptyProfile }]
        }));
    };

    const removeLinkedInProfile = (index) => {
        setReportForm(prev => ({
            ...prev,
            linkedin_profiles: prev.linkedin_profiles.filter((_, i) => i !== index)
        }));
    };

    const updateLinkedInProfile = (index, field, value) => {
        setReportForm(prev => {
            const profiles = [...prev.linkedin_profiles];
            profiles[index] = { ...profiles[index], [field]: value };
            return { ...prev, linkedin_profiles: profiles };
        });
    };

    const handleSaveReport = async () => {
        setSaving(true);
        try {
            const reportData = {
                id: editingReport?.id || undefined,
                customer_id: reportForm.customer_id,
                report_type: reportForm.report_type,
                period_start: reportForm.period_start,
                period_end: reportForm.period_end,
                metrics: {
                    target_accounts: parseInt(reportForm.target_accounts) || 0,
                    total_contacts: parseInt(reportForm.total_contacts) || 0,
                    total_reachouts: parseInt(reportForm.total_reachouts) || 0,
                    responses: parseInt(reportForm.responses) || 0,
                    positive_responses: parseInt(reportForm.positive_responses) || 0,
                    rejects: parseInt(reportForm.rejects) || 0,
                    future_leads: parseInt(reportForm.future_leads) || 0,
                    mqls: parseInt(reportForm.mqls) || 0,
                    qualified_leads: parseInt(reportForm.qualified_leads) || 0,
                },
                linkedin: {
                    target_accounts: parseInt(reportForm.linkedin_target_accounts) || 0,
                    target_contacts: parseInt(reportForm.linkedin_target_contacts) || 0,
                    reachouts_sent: parseInt(reportForm.linkedin_reachouts_sent) || 0,
                    connection_accepted: parseInt(reportForm.linkedin_connection_accepted) || 0,
                    responses: parseInt(reportForm.linkedin_responses) || 0,
                    positive_responses: parseInt(reportForm.linkedin_positive_responses) || 0,
                    rejections: parseInt(reportForm.linkedin_rejections) || 0,
                    in_progress: parseInt(reportForm.linkedin_in_progress) || 0,
                    mqls: parseInt(reportForm.linkedin_mqls) || 0,
                    qualified_leads: parseInt(reportForm.linkedin_qualified_leads) || 0,
                    profiles: reportForm.linkedin_profiles.filter(p => p.profile_name.trim()).map(p => ({
                        profile_name: p.profile_name,
                        reachouts_sent: parseInt(p.reachouts_sent) || 0,
                        connection_accepted: parseInt(p.connection_accepted) || 0,
                        responses: parseInt(p.responses) || 0,
                        positive_responses: parseInt(p.positive_responses) || 0,
                        rejections: parseInt(p.rejections) || 0,
                        in_progress: parseInt(p.in_progress) || 0,
                        mqls: parseInt(p.mqls) || 0,
                        qualified_leads: parseInt(p.qualified_leads) || 0,
                        target_industries: typeof p.target_industries === 'string' ? p.target_industries.split(',').map(s => s.trim()).filter(Boolean) : (p.target_industries || []),
                        target_company_sizes: typeof p.target_company_sizes === 'string' ? p.target_company_sizes.split(',').map(s => s.trim()).filter(Boolean) : (p.target_company_sizes || []),
                        target_designations: typeof p.target_designations === 'string' ? p.target_designations.split(',').map(s => s.trim()).filter(Boolean) : (p.target_designations || []),
                    })),
                },
                email: {
                    emails_sent: parseInt(reportForm.email_sent) || 0,
                    delivered: parseInt(reportForm.email_delivered) || 0,
                    opened: parseInt(reportForm.email_opened) || 0,
                    clicked: parseInt(reportForm.email_clicked) || 0,
                    replies: parseInt(reportForm.email_replies) || 0,
                    positive_replies: parseInt(reportForm.email_positive_replies) || 0,
                    mqls: parseInt(reportForm.email_mqls) || 0,
                    qualified_leads: parseInt(reportForm.email_qualified_leads) || 0,
                },
                ads: {
                    accounts_targeted: parseInt(reportForm.ads_accounts_targeted) || 0,
                    impressions: parseInt(reportForm.ads_impressions) || 0,
                    clicks: parseInt(reportForm.ads_clicks) || 0,
                    ctr: parseFloat(reportForm.ads_ctr) || 0,
                    account_reach_percent: parseFloat(reportForm.ads_account_reach_percent) || 0,
                    engagement_rate: parseFloat(reportForm.ads_engagement_rate) || 0,
                    cost_per_engaged_account: parseFloat(reportForm.ads_cost_per_engaged_account) || 0,
                    cost_per_mql: parseFloat(reportForm.ads_cost_per_mql) || 0,
                    influenced_pipeline: parseFloat(reportForm.ads_influenced_pipeline) || 0,
                },
                inbound: {
                    total_views: parseInt(reportForm.inbound_total_views) || 0,
                    total_engagement: parseInt(reportForm.inbound_total_engagement) || 0,
                    total_leads: parseInt(reportForm.inbound_total_leads) || 0,
                    contents: editingReport?.inbound?.contents || [],
                },
                funnel_data: {
                    reach: parseInt(reportForm.total_reachouts) || 0,
                    connect: parseInt(reportForm.responses) || 0,
                    nurture: parseInt(reportForm.positive_responses) || 0,
                    response: parseInt(reportForm.responses) || 0,
                    future_leads: parseInt(reportForm.future_leads) || 0,
                    positive_response: parseInt(reportForm.positive_responses) || 0,
                    mql: parseInt(reportForm.mqls) || 0,
                    sql: parseInt(reportForm.qualified_leads) || 0,
                },
                insights: reportForm.insights.split('\n').filter(s => s.trim()),
                risks_blockers: reportForm.risks_blockers.split('\n').filter(s => s.trim()),
                recommendations: reportForm.recommendations.split('\n').filter(s => s.trim()),
                goals: {
                    target_accounts: parseInt(reportForm.goal_target_accounts) || 0,
                    total_contacts: parseInt(reportForm.goal_total_contacts) || 0,
                    total_reachouts: parseInt(reportForm.goal_total_reachouts) || 0,
                    responses: parseInt(reportForm.goal_responses) || 0,
                    target_mqls: parseInt(reportForm.goal_mqls) || 0,
                    target_qualified_leads: parseInt(reportForm.goal_qualified_leads) || 0,
                },
            };

            if (editingReport) {
                await reportsApi.update(editingReport.id, reportData);
                toast.success('Report updated successfully');
            } else {
                await reportsApi.create(reportData);
                toast.success('Report created successfully');
            }
            
            setDialogOpen(false);
            loadData();
        } catch (error) {
            toast.error('Failed to save report');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteReport = async (reportId) => {
        if (!window.confirm('Are you sure you want to delete this report?')) return;
        
        try {
            await reportsApi.delete(reportId);
            toast.success('Report deleted');
            loadData();
        } catch (error) {
            toast.error('Failed to delete report');
        }
    };

    const handleCsvImport = async () => {
        if (!csvFile || !selectedCustomer) {
            toast.error('Please select a customer and CSV file');
            return;
        }

        setSaving(true);
        try {
            const formData = new FormData();
            formData.append('file', csvFile);
            formData.append('customer_id', selectedCustomer);
            formData.append('report_type', 'weekly');

            const response = await reportsApi.importCsv(formData);
            toast.success(`Imported ${response.data.imported} reports`);
            if (response.data.errors?.length > 0) {
                toast.error(`${response.data.errors.length} errors occurred`);
            }
            setCsvFile(null);
            loadData();
        } catch (error) {
            toast.error('Failed to import CSV');
        } finally {
            setSaving(false);
        }
    };

    const downloadCsvTemplate = async () => {
        try {
            const response = await reportsApi.getCsvTemplate();
            const { columns, example_row } = response.data;
            
            let csv = columns.join(',') + '\n';
            csv += columns.map(col => example_row[col] || '').join(',');
            
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'report_import_template.csv';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            toast.success('Template downloaded');
        } catch (error) {
            toast.error('Failed to download template');
        }
    };

    const updateForm = (field, value) => {
        setReportForm(prev => ({ ...prev, [field]: value }));
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-brand-red/30 border-t-brand-red rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen" data-testid="admin-reports-page">
            <Header 
                title="Report Management"
                subtitle="Create, edit, and import weekly/monthly reports"
            />

            <div className="p-8 space-y-6">
                {/* Actions Bar */}
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-4">
                        <Select
                            value={selectedCustomer || "all"}
                            onValueChange={(v) => setSelectedCustomer(v === "all" ? "" : v)}
                        >
                            <SelectTrigger className="input-base w-64">
                                <SelectValue placeholder="All Customers" />
                            </SelectTrigger>
                            <SelectContent className="bg-surface-card border-line-default">
                                <SelectItem value="all">All Customers</SelectItem>
                                {customers.filter(c => c.details?.id).map((c) => (
                                    <SelectItem key={c.user.id} value={c.details.id}>
                                        {c.details?.company_name || c.user.company_name || c.user.email}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <span className="text-content-secondary">
                            {reports.length} report{reports.length !== 1 ? 's' : ''}
                        </span>
                    </div>

                    <div className="flex items-center gap-3">
                        <input
                            ref={csvInputRef}
                            type="file"
                            accept=".csv"
                            className="hidden"
                            onChange={(e) => setCsvFile(e.target.files[0])}
                        />
                        <Button variant="ghost" className="btn-ghost gap-2" onClick={downloadCsvTemplate}>
                            <Download className="w-4 h-4" />
                            Template
                        </Button>
                        <Button variant="ghost" className="btn-ghost gap-2" onClick={() => csvInputRef.current?.click()}>
                            <FileSpreadsheet className="w-4 h-4" />
                            {csvFile ? csvFile.name : 'Select CSV'}
                        </Button>
                        {csvFile && (
                            <Button className="btn-primary gap-2" onClick={handleCsvImport} disabled={saving || !selectedCustomer}>
                                <Upload className="w-4 h-4" />
                                Import
                            </Button>
                        )}
                        <Button className="btn-primary gap-2" onClick={handleOpenCreate} data-testid="create-report-btn">
                            <Plus className="w-4 h-4" />
                            NEW REPORT
                        </Button>
                    </div>
                </div>

                {/* Reports Table */}
                <div className="card-base overflow-hidden">
                    <table className="w-full data-table">
                        <thead>
                            <tr>
                                <th className="text-left p-4 text-content-muted">Period</th>
                                <th className="text-left p-4 text-content-muted">Customer</th>
                                <th className="text-left p-4 text-content-muted">Type</th>
                                <th className="text-center p-4 text-content-muted">MQLs</th>
                                <th className="text-center p-4 text-content-muted">SQLs</th>
                                <th className="text-center p-4 text-content-muted">Profiles</th>
                                <th className="text-right p-4 text-content-muted">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reports.map((report) => {
                                const customer = customers.find(c => c.details?.id === report.customer_id);
                                const profileCount = report.linkedin?.profiles?.length || 0;
                                return (
                                    <tr key={report.id}>
                                        <td className="p-4">
                                            <div className="flex items-center gap-2">
                                                <Calendar className="w-4 h-4 text-content-muted" />
                                                <span className="text-white text-sm">
                                                    {formatDateRange(report.period_start, report.period_end)}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="p-4 text-content-secondary">
                                            {customer?.details?.company_name || 'Unknown'}
                                        </td>
                                        <td className="p-4">
                                            <Badge className={`badge ${report.report_type === 'weekly' ? 'badge-info' : 'badge-success'}`}>
                                                {report.report_type}
                                            </Badge>
                                        </td>
                                        <td className="p-4 text-center">
                                            <span className="text-brand-red font-bold">{report.metrics?.mqls || 0}</span>
                                        </td>
                                        <td className="p-4 text-center">
                                            <span className="text-status-success font-bold">{report.metrics?.qualified_leads || 0}</span>
                                        </td>
                                        <td className="p-4 text-center">
                                            <Badge className="badge badge-info">{profileCount} profiles</Badge>
                                        </td>
                                        <td className="p-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button variant="ghost" size="sm" className="btn-ghost" onClick={() => handleOpenEdit(report)} data-testid={`edit-report-${report.id}`}>
                                                    <Edit className="w-4 h-4" />
                                                </Button>
                                                <Button variant="ghost" size="sm" className="btn-ghost text-status-error hover:text-status-error" onClick={() => handleDeleteReport(report.id)}>
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {reports.length === 0 && (
                        <div className="text-center py-12">
                            <FileText className="w-12 h-12 text-content-muted mx-auto mb-4" />
                            <p className="text-content-secondary">No reports found</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-surface-card border-line-default max-w-5xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-white font-heading">
                            {editingReport ? 'Edit Report' : 'Create New Report'}
                        </DialogTitle>
                    </DialogHeader>

                    <Tabs defaultValue="general" className="mt-4">
                        <TabsList className="bg-surface-app border border-line-default flex-wrap h-auto gap-1 p-1">
                            <TabsTrigger value="general" className="data-[state=active]:bg-brand-red data-[state=active]:text-white">
                                General
                            </TabsTrigger>
                            <TabsTrigger value="linkedin" className="data-[state=active]:bg-channel-linkedin data-[state=active]:text-white gap-1">
                                <Linkedin className="w-3 h-3" /> LinkedIn
                            </TabsTrigger>
                            <TabsTrigger value="profiles" className="data-[state=active]:bg-channel-linkedin data-[state=active]:text-white gap-1">
                                <UserPlus className="w-3 h-3" /> Profiles
                            </TabsTrigger>
                            <TabsTrigger value="email" className="data-[state=active]:bg-channel-email data-[state=active]:text-white gap-1">
                                <Mail className="w-3 h-3" /> Email
                            </TabsTrigger>
                            <TabsTrigger value="ads" className="data-[state=active]:bg-channel-ads data-[state=active]:text-white gap-1">
                                <Megaphone className="w-3 h-3" /> Ads
                            </TabsTrigger>
                            <TabsTrigger value="inbound" className="data-[state=active]:bg-channel-inbound data-[state=active]:text-white gap-1">
                                <TrendingUp className="w-3 h-3" /> Inbound
                            </TabsTrigger>
                            <TabsTrigger value="insights" className="data-[state=active]:bg-brand-red data-[state=active]:text-white">
                                Insights
                            </TabsTrigger>
                            <TabsTrigger value="goals" className="data-[state=active]:bg-brand-red data-[state=active]:text-white">
                                Goals
                            </TabsTrigger>
                        </TabsList>

                        {/* General Tab */}
                        <TabsContent value="general" className="mt-4 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-content-secondary">Customer *</Label>
                                    <Select value={reportForm.customer_id} onValueChange={(v) => updateForm('customer_id', v)}>
                                        <SelectTrigger className="input-base">
                                            <SelectValue placeholder="Select customer" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-surface-card border-line-default">
                                            {customers.filter(c => c.details?.id).map((c) => (
                                                <SelectItem key={c.user.id} value={c.details.id}>
                                                    {c.details?.company_name || c.user.company_name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-content-secondary">Report Type *</Label>
                                    <Select value={reportForm.report_type} onValueChange={(v) => updateForm('report_type', v)}>
                                        <SelectTrigger className="input-base">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-surface-card border-line-default">
                                            <SelectItem value="weekly">Weekly</SelectItem>
                                            <SelectItem value="monthly">Monthly</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-content-secondary">Period Start *</Label>
                                    <Input type="date" value={reportForm.period_start} onChange={(e) => updateForm('period_start', e.target.value)} className="input-base" />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-content-secondary">Period End *</Label>
                                    <Input type="date" value={reportForm.period_end} onChange={(e) => updateForm('period_end', e.target.value)} className="input-base" />
                                </div>
                            </div>
                        </TabsContent>

                        {/* LinkedIn Tab - Overall */}
                        <TabsContent value="linkedin" className="mt-4">
                            <div className="p-4 rounded-lg bg-channel-linkedin/10 border border-channel-linkedin/30 mb-4">
                                <h4 className="text-channel-linkedin font-bold flex items-center gap-2">
                                    <Linkedin className="w-4 h-4" /> LinkedIn Overall Metrics
                                </h4>
                                <p className="text-xs text-content-muted mt-1">Aggregate metrics across all profiles</p>
                            </div>
                            <div className="grid grid-cols-5 gap-4">
                                <NumericField label="Target Accounts" value={reportForm.linkedin_target_accounts} onChange={(v) => updateForm('linkedin_target_accounts', v)} />
                                <NumericField label="Target Contacts" value={reportForm.linkedin_target_contacts} onChange={(v) => updateForm('linkedin_target_contacts', v)} />
                                <NumericField label="Reachouts Sent" value={reportForm.linkedin_reachouts_sent} onChange={(v) => updateForm('linkedin_reachouts_sent', v)} />
                                <NumericField label="Connection Accepted" value={reportForm.linkedin_connection_accepted} onChange={(v) => updateForm('linkedin_connection_accepted', v)} />
                                <NumericField label="Responses" value={reportForm.linkedin_responses} onChange={(v) => updateForm('linkedin_responses', v)} />
                                <NumericField label="Positive Responses" value={reportForm.linkedin_positive_responses} onChange={(v) => updateForm('linkedin_positive_responses', v)} />
                                <NumericField label="Rejections" value={reportForm.linkedin_rejections} onChange={(v) => updateForm('linkedin_rejections', v)} />
                                <NumericField label="In Progress" value={reportForm.linkedin_in_progress} onChange={(v) => updateForm('linkedin_in_progress', v)} />
                                <div className="space-y-1">
                                    <Label className="text-brand-red text-xs font-bold">MQLs</Label>
                                    <Input type="text" inputMode="numeric" value={reportForm.linkedin_mqls} onChange={(e) => updateForm('linkedin_mqls', e.target.value)} className="input-base h-9 border-brand-red/50" />
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-status-success text-xs font-bold">Qualified Leads</Label>
                                    <Input type="text" inputMode="numeric" value={reportForm.linkedin_qualified_leads} onChange={(e) => updateForm('linkedin_qualified_leads', e.target.value)} className="input-base h-9 border-status-success/50" />
                                </div>
                            </div>
                        </TabsContent>

                        {/* LinkedIn Profiles Tab */}
                        <TabsContent value="profiles" className="mt-4">
                            <div className="p-4 rounded-lg bg-channel-linkedin/10 border border-channel-linkedin/30 mb-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h4 className="text-channel-linkedin font-bold flex items-center gap-2">
                                            <UserPlus className="w-4 h-4" /> LinkedIn Profile-wise Data
                                        </h4>
                                        <p className="text-xs text-content-muted mt-1">Add metrics for each LinkedIn profile</p>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button onClick={async () => {
                                            try {
                                                const res = await reportsApi.getLinkedInProfiles();
                                                const prevProfiles = res.data.profiles || [];
                                                if (prevProfiles.length === 0) {
                                                    toast.info('No previous profiles found');
                                                    return;
                                                }
                                                const existingNames = reportForm.linkedin_profiles.map(p => p.profile_name);
                                                const newProfiles = prevProfiles.filter(p => !existingNames.includes(p.profile_name)).map(p => ({
                                                    ...emptyProfile,
                                                    profile_name: p.profile_name,
                                                    target_industries: (p.target_industries || []).join(', '),
                                                    target_company_sizes: (p.target_company_sizes || []).join(', '),
                                                    target_designations: (p.target_designations || []).join(', '),
                                                }));
                                                if (newProfiles.length === 0) {
                                                    toast.info('All profiles already added');
                                                    return;
                                                }
                                                setReportForm(prev => ({
                                                    ...prev,
                                                    linkedin_profiles: [...prev.linkedin_profiles, ...newProfiles],
                                                }));
                                                toast.success(`Loaded ${newProfiles.length} profile(s) from previous reports`);
                                            } catch (e) {
                                                toast.error('Failed to load profiles');
                                            }
                                        }} variant="outline" className="btn-secondary gap-2" size="sm" data-testid="load-prev-profiles-btn">
                                            <RefreshCw className="w-3 h-3" /> Load Previous Profiles
                                        </Button>
                                        <Button onClick={addLinkedInProfile} className="btn-primary gap-2" size="sm">
                                            <Plus className="w-4 h-4" /> Add Profile
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {reportForm.linkedin_profiles.length === 0 ? (
                                <div className="text-center py-8 border border-dashed border-line-default rounded-lg">
                                    <UserPlus className="w-12 h-12 text-content-muted mx-auto mb-3" />
                                    <p className="text-content-secondary mb-3">No LinkedIn profiles added yet</p>
                                    <Button onClick={addLinkedInProfile} variant="outline" className="btn-secondary">
                                        <Plus className="w-4 h-4 mr-2" /> Add First Profile
                                    </Button>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {reportForm.linkedin_profiles.map((profile, index) => (
                                        <div key={index} className="p-4 border border-line-default rounded-lg bg-surface-app">
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-channel-linkedin/20 flex items-center justify-center text-channel-linkedin font-bold">
                                                        {index + 1}
                                                    </div>
                                                    <Input
                                                        placeholder="Profile Name (e.g., John Smith, Sales Team, etc.)"
                                                        value={profile.profile_name}
                                                        onChange={(e) => updateLinkedInProfile(index, 'profile_name', e.target.value)}
                                                        className="input-base w-64"
                                                    />
                                                </div>
                                                <Button variant="ghost" size="sm" className="btn-ghost text-status-error" onClick={() => removeLinkedInProfile(index)}>
                                                    <X className="w-4 h-4" />
                                                </Button>
                                            </div>

                                            {/* Target Audience */}
                                            <div className="grid grid-cols-3 gap-3 mb-3 p-3 rounded bg-surface-card border border-line-default">
                                                <div className="space-y-1">
                                                    <Label className="text-channel-linkedin text-xs font-bold">Target Industries</Label>
                                                    <Input placeholder="IT, Healthcare, Finance" value={profile.target_industries || ''} onChange={(e) => updateLinkedInProfile(index, 'target_industries', e.target.value)} className="input-base h-8 text-xs" />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-channel-ads text-xs font-bold">Company Sizes</Label>
                                                    <Input placeholder="500-1000, 1000-5000" value={profile.target_company_sizes || ''} onChange={(e) => updateLinkedInProfile(index, 'target_company_sizes', e.target.value)} className="input-base h-8 text-xs" />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-brand-red text-xs font-bold">Target Designations</Label>
                                                    <Input placeholder="VP Sales, CTO, Director" value={profile.target_designations || ''} onChange={(e) => updateLinkedInProfile(index, 'target_designations', e.target.value)} className="input-base h-8 text-xs" />
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-4 gap-3">
                                                <div className="space-y-1">
                                                    <Label className="text-content-muted text-xs">Reachouts Sent</Label>
                                                    <Input type="text" inputMode="numeric" value={profile.reachouts_sent} onChange={(e) => updateLinkedInProfile(index, 'reachouts_sent', e.target.value)} className="input-base h-8" />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-content-muted text-xs">Accepted</Label>
                                                    <Input type="text" inputMode="numeric" value={profile.connection_accepted} onChange={(e) => updateLinkedInProfile(index, 'connection_accepted', e.target.value)} className="input-base h-8" />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-content-muted text-xs">Responses</Label>
                                                    <Input type="text" inputMode="numeric" value={profile.responses} onChange={(e) => updateLinkedInProfile(index, 'responses', e.target.value)} className="input-base h-8" />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-content-muted text-xs">Positive</Label>
                                                    <Input type="text" inputMode="numeric" value={profile.positive_responses} onChange={(e) => updateLinkedInProfile(index, 'positive_responses', e.target.value)} className="input-base h-8" />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-content-muted text-xs">Rejections</Label>
                                                    <Input type="text" inputMode="numeric" value={profile.rejections} onChange={(e) => updateLinkedInProfile(index, 'rejections', e.target.value)} className="input-base h-8" />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-content-muted text-xs">In Progress</Label>
                                                    <Input type="text" inputMode="numeric" value={profile.in_progress} onChange={(e) => updateLinkedInProfile(index, 'in_progress', e.target.value)} className="input-base h-8" />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-brand-red text-xs font-bold">MQLs</Label>
                                                    <Input type="text" inputMode="numeric" value={profile.mqls} onChange={(e) => updateLinkedInProfile(index, 'mqls', e.target.value)} className="input-base h-8 border-brand-red/30" />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-status-success text-xs font-bold">SQLs</Label>
                                                    <Input type="text" inputMode="numeric" value={profile.qualified_leads} onChange={(e) => updateLinkedInProfile(index, 'qualified_leads', e.target.value)} className="input-base h-8 border-status-success/30" />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </TabsContent>

                        {/* Email Tab */}
                        <TabsContent value="email" className="mt-4">
                            <div className="p-4 rounded-lg bg-channel-email/10 border border-channel-email/30 mb-4">
                                <h4 className="text-channel-email font-bold flex items-center gap-2">
                                    <Mail className="w-4 h-4" /> Email Outbound / Nurture Metrics
                                </h4>
                            </div>
                            <div className="grid grid-cols-4 gap-4">
                                <NumericField label="Emails Sent" value={reportForm.email_sent} onChange={(v) => updateForm('email_sent', v)} />
                                <NumericField label="Delivered" value={reportForm.email_delivered} onChange={(v) => updateForm('email_delivered', v)} />
                                <NumericField label="Opened" value={reportForm.email_opened} onChange={(v) => updateForm('email_opened', v)} />
                                <NumericField label="Clicked" value={reportForm.email_clicked} onChange={(v) => updateForm('email_clicked', v)} />
                                <NumericField label="Replies" value={reportForm.email_replies} onChange={(v) => updateForm('email_replies', v)} />
                                <NumericField label="Positive Replies" value={reportForm.email_positive_replies} onChange={(v) => updateForm('email_positive_replies', v)} />
                                <div className="space-y-1">
                                    <Label className="text-brand-red text-xs font-bold">MQLs</Label>
                                    <Input type="text" inputMode="numeric" value={reportForm.email_mqls} onChange={(e) => updateForm('email_mqls', e.target.value)} className="input-base h-9 border-brand-red/50" />
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-status-success text-xs font-bold">Qualified Leads</Label>
                                    <Input type="text" inputMode="numeric" value={reportForm.email_qualified_leads} onChange={(e) => updateForm('email_qualified_leads', e.target.value)} className="input-base h-9 border-status-success/50" />
                                </div>
                            </div>
                        </TabsContent>

                        {/* Ads Tab */}
                        <TabsContent value="ads" className="mt-4">
                            <div className="p-4 rounded-lg bg-channel-ads/10 border border-channel-ads/30 mb-4">
                                <h4 className="text-channel-ads font-bold flex items-center gap-2">
                                    <Megaphone className="w-4 h-4" /> Account-Based Advertising Metrics
                                </h4>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <NumericField label="Accounts Targeted" value={reportForm.ads_accounts_targeted} onChange={(v) => updateForm('ads_accounts_targeted', v)} />
                                <NumericField label="Impressions" value={reportForm.ads_impressions} onChange={(v) => updateForm('ads_impressions', v)} />
                                <NumericField label="Clicks" value={reportForm.ads_clicks} onChange={(v) => updateForm('ads_clicks', v)} />
                                <NumericField label="CTR (%)" value={reportForm.ads_ctr} onChange={(v) => updateForm('ads_ctr', v)} />
                                <NumericField label="Account Reach (%)" value={reportForm.ads_account_reach_percent} onChange={(v) => updateForm('ads_account_reach_percent', v)} />
                                <NumericField label="Engagement Rate (%)" value={reportForm.ads_engagement_rate} onChange={(v) => updateForm('ads_engagement_rate', v)} />
                                <NumericField label="Cost per Engaged Account ($)" value={reportForm.ads_cost_per_engaged_account} onChange={(v) => updateForm('ads_cost_per_engaged_account', v)} />
                                <NumericField label="Cost per MQL ($)" value={reportForm.ads_cost_per_mql} onChange={(v) => updateForm('ads_cost_per_mql', v)} />
                                <NumericField label="Influenced Pipeline ($)" value={reportForm.ads_influenced_pipeline} onChange={(v) => updateForm('ads_influenced_pipeline', v)} />
                            </div>
                        </TabsContent>

                        {/* Inbound Tab */}
                        <TabsContent value="inbound" className="mt-4">
                            <div className="p-4 rounded-lg bg-channel-inbound/10 border border-channel-inbound/30 mb-4">
                                <h4 className="text-channel-inbound font-bold flex items-center gap-2">
                                    <TrendingUp className="w-4 h-4" /> Inbound Content Metrics
                                </h4>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <NumericField label="Total Views" value={reportForm.inbound_total_views} onChange={(v) => updateForm('inbound_total_views', v)} />
                                <NumericField label="Total Engagement (Likes + Shares + Comments)" value={reportForm.inbound_total_engagement} onChange={(v) => updateForm('inbound_total_engagement', v)} />
                                <NumericField label="Leads Generated" value={reportForm.inbound_total_leads} onChange={(v) => updateForm('inbound_total_leads', v)} />
                            </div>
                        </TabsContent>

                        {/* Insights Tab */}
                        <TabsContent value="insights" className="mt-4 space-y-4">
                            <div className="space-y-2">
                                <Label className="text-content-secondary">Insights & Observations (one per line)</Label>
                                <Textarea
                                    value={reportForm.insights}
                                    onChange={(e) => updateForm('insights', e.target.value)}
                                    className="input-base min-h-[100px]"
                                    placeholder="LinkedIn outreach showing strong engagement...&#10;Email open rates improved by 15%..."
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-content-secondary">Risks & Blockers (one per line)</Label>
                                <Textarea
                                    value={reportForm.risks_blockers}
                                    onChange={(e) => updateForm('risks_blockers', e.target.value)}
                                    className="input-base min-h-[80px]"
                                    placeholder="Healthcare segment showing slower response...&#10;Need more case studies..."
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-content-secondary">Recommendations (one per line)</Label>
                                <Textarea
                                    value={reportForm.recommendations}
                                    onChange={(e) => updateForm('recommendations', e.target.value)}
                                    className="input-base min-h-[100px]"
                                    placeholder="Increase LinkedIn cadence for Tier 1...&#10;A/B test email templates..."
                                />
                            </div>
                        </TabsContent>

                        {/* Goals Tab */}
                        <TabsContent value="goals" className="mt-4 space-y-4">
                            <div className="p-4 rounded-lg bg-brand-red/10 border border-brand-red/30 mb-2">
                                <h4 className="text-brand-red font-bold">Goal Targets</h4>
                                <p className="text-xs text-content-muted mt-1">Set target goals for this period. These will be compared against actual performance in the customer view.</p>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <NumericField label="Target Accounts Goal" value={reportForm.goal_target_accounts} onChange={(v) => updateForm('goal_target_accounts', v)} />
                                <NumericField label="Total Contacts Goal" value={reportForm.goal_total_contacts} onChange={(v) => updateForm('goal_total_contacts', v)} />
                                <NumericField label="Total Reachouts Goal" value={reportForm.goal_total_reachouts} onChange={(v) => updateForm('goal_total_reachouts', v)} />
                                <NumericField label="Responses Goal" value={reportForm.goal_responses} onChange={(v) => updateForm('goal_responses', v)} />
                                <div className="space-y-1">
                                    <Label className="text-brand-red text-xs font-bold">MQLs Goal</Label>
                                    <Input type="text" inputMode="numeric" value={reportForm.goal_mqls} onChange={(e) => updateForm('goal_mqls', e.target.value)} className="input-base h-9 border-brand-red/50" />
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-status-success text-xs font-bold">Qualified Leads Goal</Label>
                                    <Input type="text" inputMode="numeric" value={reportForm.goal_qualified_leads} onChange={(e) => updateForm('goal_qualified_leads', e.target.value)} className="input-base h-9 border-status-success/50" />
                                </div>
                            </div>
                        </TabsContent>
                    </Tabs>

                    <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-line-default">
                        <Button variant="ghost" onClick={() => setDialogOpen(false)} className="btn-ghost">
                            Cancel
                        </Button>
                        <Button onClick={handleSaveReport} disabled={saving} className="btn-primary">
                            {saving ? 'Saving...' : editingReport ? 'Update Report' : 'Create Report'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
